<script setup>
import { toast } from 'dashboard/composables/useToast';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AgentsAPI from 'dashboard/api/agents';
import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);
const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const permissionLabel = permission =>
  t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`);

// Lista de permissoes do papel. Vai por Teleport + position fixed porque a
// tabela rola de lado: popover absoluto dentro da linha era cortado pelo
// container de scroll (era por isso que o scroll ficava desligado no desktop,
// e ai a coluna que nao coubesse era fatiada em silencio).
const permissoesPopover = ref({ visivel: false, x: 0, y: 0, itens: [] });

const abrirPermissoes = (evento, agent) => {
  const itens = getAgentRolePermissions(agent);
  if (!itens.length) return;

  const alvo = evento.currentTarget.getBoundingClientRect();
  permissoesPopover.value = {
    visivel: true,
    x: alvo.left,
    y: alvo.bottom + 8,
    itens,
  };
};

const fecharPermissoes = () => {
  permissoesPopover.value = { ...permissoesPopover.value, visivel: false };
};

const tableHeaders = computed(() => [
  { label: t('AGENT_MGMT.LIST.TABLE_HEADER.NAME'), key: 'name' },
  { label: t('AGENT_MGMT.LIST.TABLE_HEADER.ROLE'), key: 'role' },
  {
    label: t('AGENT_MGMT.LIST.TABLE_HEADER.INBOXES'),
    align: 'right',
    key: 'inboxes',
  },
  {
    label: t('AGENT_MGMT.LIST.TABLE_HEADER.TEAMS'),
    align: 'right',
    key: 'teams',
  },
  {
    label: t('AGENT_MGMT.LIST.TABLE_HEADER.OPEN'),
    align: 'right',
    key: 'open',
  },
  {
    label: t('AGENT_MGMT.LIST.TABLE_HEADER.RESOLVED_7D'),
    align: 'right',
    key: 'resolved_7d',
  },
  { label: t('AGENT_MGMT.LIST.TABLE_HEADER.LAST_ACTIVE'), key: 'active_at' },
  t('AGENT_MGMT.LIST.TABLE_HEADER.STATUS'),
  '',
]);

// ── O retrato do time (endpoint separado do /agents) ──
const retrato = ref({});
const carregandoRetrato = ref(true);
const fetchRetrato = async () => {
  try {
    const {
      data: { payload },
    } = await AgentsAPI.overview();
    retrato.value = Object.fromEntries(payload.map(linha => [linha.id, linha]));
  } catch {
    retrato.value = {};
  } finally {
    carregandoRetrato.value = false;
  }
};

const dadosDe = agent => retrato.value[agent.id] || {};

// ── Busca, filtro e ordem ──
const busca = ref('');
const filtro = ref('todos');
const ordem = ref({ campo: 'name', asc: true });
const expandidos = ref(new Set());

const FILTROS = ['todos', 'atendendo', 'espera', 'parados', 'semcaixa'];

const diasSem = agent => {
  const quando = dadosDe(agent).active_at;
  if (!quando) return null;
  return Math.floor((Date.now() - new Date(quando).getTime()) / 86400000);
};

const passaFiltro = agent => {
  const d = dadosDe(agent);
  if (filtro.value === 'atendendo') return (d.open || 0) > 0;
  if (filtro.value === 'espera') return (d.waiting || 0) > 0;
  if (filtro.value === 'parados') {
    const dias = diasSem(agent);
    return dias === null || dias >= 30 || !agent.confirmed;
  }
  if (filtro.value === 'semcaixa') return !(d.inboxes || []).length;
  return true;
};

const valorDe = (agent, campo) => {
  const d = dadosDe(agent);
  if (campo === 'name') return agent.name.toLowerCase();
  if (campo === 'role') return getAgentRoleName(agent).toLowerCase();
  if (campo === 'inboxes') return (d.inboxes || []).length;
  if (campo === 'teams') return (d.teams || []).length;
  if (campo === 'active_at')
    return d.active_at ? new Date(d.active_at).getTime() : 0;
  return d[campo] || 0;
};

const linhas = computed(() => {
  const termo = busca.value.trim().toLowerCase();
  const filtradas = agentList.value.filter(
    agent =>
      passaFiltro(agent) &&
      (!termo ||
        agent.name.toLowerCase().includes(termo) ||
        agent.email.toLowerCase().includes(termo))
  );
  const dir = ordem.value.asc ? 1 : -1;
  return [...filtradas].sort((x, y) => {
    const a = valorDe(x, ordem.value.campo);
    const b = valorDe(y, ordem.value.campo);
    if (a === b) return x.name.localeCompare(y.name);
    return a > b ? dir : -dir;
  });
});

const ordenarPor = campo => {
  const numerico = [
    'inboxes',
    'teams',
    'open',
    'resolved_7d',
    'active_at',
  ].includes(campo);
  if (ordem.value.campo === campo) ordem.value.asc = !ordem.value.asc;
  else ordem.value = { campo, asc: !numerico };
};

const alternar = agent => {
  const marcados = new Set(expandidos.value);
  if (marcados.has(agent.id)) marcados.delete(agent.id);
  else marcados.add(agent.id);
  expandidos.value = marcados;
};

// ── Resumo do topo: só o que pede ação ──
const resumo = computed(() => {
  const linhasRetrato = agentList.value.map(dadosDe);
  const soma = campo =>
    linhasRetrato.reduce((total, d) => total + (d[campo] || 0), 0);
  return [
    { n: agentList.value.length, label: t('AGENT_MGMT.LIST.SUMMARY.AGENTS') },
    { n: soma('open'), label: t('AGENT_MGMT.LIST.SUMMARY.OPEN') },
    {
      n: soma('waiting'),
      label: t('AGENT_MGMT.LIST.SUMMARY.WAITING'),
      alerta: true,
    },
    {
      n: agentList.value.filter(agent => !dadosDe(agent).active_at).length,
      label: t('AGENT_MGMT.LIST.SUMMARY.NEVER'),
      alerta: true,
    },
    {
      n: agentList.value.filter(agent => !agent.confirmed).length,
      label: t('AGENT_MGMT.LIST.SUMMARY.PENDING'),
      alerta: true,
    },
  ];
});

// "hoje" / "ontem" / "há 6 dias" / "há 3 meses" / "nunca abriu"
const ultimaAtividade = agent => {
  const dias = diasSem(agent);
  if (dias === null)
    return { texto: t('AGENT_MGMT.LIST.NEVER_OPENED'), frio: true };
  if (dias <= 0) return { texto: t('AGENT_MGMT.LIST.TODAY'), frio: false };
  if (dias === 1) return { texto: t('AGENT_MGMT.LIST.YESTERDAY'), frio: false };
  if (dias < 30)
    return { texto: t('AGENT_MGMT.LIST.DAYS_AGO', { n: dias }), frio: false };
  return {
    texto: t('AGENT_MGMT.LIST.MONTHS_AGO', {
      n: Math.max(1, Math.floor(dias / 30)),
    }),
    frio: true,
  };
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
  fetchRetrato();
});

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = (message, kind = 'error') => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  toast[kind](message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'), 'success');
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsPageLayout
    wide
    :title="$t('AGENT_MGMT.HEADER')"
    :description="$t('AGENT_MGMT.DESCRIPTION')"
  >
    <template #actions>
      <Button
        icon="i-lucide-circle-plus"
        :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
        @click="openAddPopup"
      />
    </template>

    <div
      class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-n-weak border border-n-weak rounded-xl overflow-hidden mb-4"
    >
      <div
        v-for="bloco in resumo"
        :key="bloco.label"
        class="bg-n-solid-1 px-4 py-3"
      >
        <div
          class="text-xl font-bold tabular-nums tracking-tight"
          :class="
            bloco.alerta && bloco.n ? 'text-n-amber-11' : 'text-n-slate-12'
          "
        >
          {{ bloco.n }}
        </div>
        <div
          class="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-n-slate-10"
        >
          {{ bloco.label }}
        </div>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-2 mb-3">
      <input
        v-model="busca"
        type="search"
        class="flex-1 min-w-[180px] max-w-xs rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-1.5 text-sm text-n-slate-12 placeholder:text-n-slate-9"
        :placeholder="$t('AGENT_MGMT.LIST.SEARCH')"
      />
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="chave in FILTROS"
          :key="chave"
          class="rounded-full border px-3 py-1 text-xs font-semibold transition-colors"
          :class="
            filtro === chave
              ? 'border-n-brand bg-n-alpha-2 text-n-brand'
              : 'border-n-weak text-n-slate-11 hover:text-n-slate-12'
          "
          @click="filtro = chave"
        >
          {{ $t(`AGENT_MGMT.LIST.FILTERS.${chave.toUpperCase()}`) }}
        </button>
      </div>
    </div>

    <SettingsTable
      dense
      :headers="tableHeaders"
      :is-loading="uiFlags.isFetching || carregandoRetrato"
      :is-empty="!linhas.length"
      :empty-message="$t('AGENT_MGMT.LIST.404')"
      :sort-key="ordem.campo"
      :sort-asc="ordem.asc"
      @sort="ordenarPor"
    >
      <template v-for="(agent, index) in linhas" :key="agent.email">
        <tr
          class="hover:bg-n-solid-3 transition-colors cursor-pointer"
          @click="alternar(agent)"
        >
          <td class="px-3 py-3">
            <div class="flex flex-row items-center gap-3 min-w-0 max-w-[17rem]">
              <span
                class="i-lucide-chevron-right size-3.5 shrink-0 text-n-slate-9 transition-transform"
                :class="{ 'rotate-90': expandidos.has(agent.id) }"
              />
              <Avatar
                :src="agent.thumbnail"
                :name="agent.name"
                :status="agent.availability_status"
                :size="36"
                hide-offline-status
                rounded-full
              />
              <div class="min-w-0">
                <span
                  class="block text-sm font-medium text-n-slate-12 capitalize truncate"
                >
                  {{ agent.name }}
                  <span
                    v-if="dadosDe(agent).administrator"
                    v-tooltip.top="$t('AGENT_MGMT.LIST.ACCOUNT_ADMIN')"
                    class="i-lucide-crown size-3 ml-0.5 align-middle text-n-amber-11"
                  />
                </span>
                <span class="block text-xs text-n-slate-10 truncate">{{
                  agent.email
                }}</span>
              </div>
            </div>
          </td>

          <td class="px-3 py-3">
            <span
              class="text-sm text-n-slate-11 font-medium w-fit block"
              :class="{
                'hover:text-n-slate-12 cursor-pointer': agent.custom_role_id,
              }"
              @mouseenter="abrirPermissoes($event, agent)"
              @mouseleave="fecharPermissoes"
            >
              {{ getAgentRoleName(agent) }}
            </span>
          </td>

          <td class="px-3 py-3 text-right text-sm tabular-nums">
            <span
              v-if="(dadosDe(agent).inboxes || []).length"
              class="text-n-slate-12"
            >
              {{ dadosDe(agent).inboxes.length }}
            </span>
            <span v-else class="text-xs font-semibold text-n-amber-11">
              {{ $t('AGENT_MGMT.LIST.NO_INBOX') }}
            </span>
          </td>

          <td class="px-3 py-3 text-right text-sm tabular-nums">
            <span
              v-if="(dadosDe(agent).teams || []).length"
              class="text-n-slate-12"
            >
              {{ dadosDe(agent).teams.length }}
            </span>
            <span v-else class="text-n-slate-9">—</span>
          </td>

          <td class="px-3 py-3 text-right">
            <div v-if="dadosDe(agent).open" class="flex flex-col items-end">
              <span class="text-sm font-bold tabular-nums text-n-slate-12">
                {{ dadosDe(agent).open }}
              </span>
              <span
                v-if="dadosDe(agent).waiting"
                class="text-[11px] font-semibold text-n-amber-11"
              >
                {{
                  $t('AGENT_MGMT.LIST.WAITING_COUNT', {
                    n: dadosDe(agent).waiting,
                  })
                }}
              </span>
            </div>
            <span v-else class="text-n-slate-9">—</span>
          </td>

          <td class="px-3 py-3 text-right text-sm tabular-nums">
            <span v-if="dadosDe(agent).resolved_7d" class="text-n-slate-11">
              {{ dadosDe(agent).resolved_7d }}
            </span>
            <span v-else class="text-n-slate-9">—</span>
          </td>

          <td class="px-3 py-3 whitespace-nowrap text-sm">
            <span
              :class="
                ultimaAtividade(agent).frio
                  ? 'text-n-slate-9'
                  : 'text-n-slate-11'
              "
            >
              {{ ultimaAtividade(agent).texto }}
            </span>
          </td>

          <td class="px-3 py-3">
            <span
              v-if="agent.confirmed"
              class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-n-teal-3 text-n-teal-11"
            >
              {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
            </span>
            <span
              v-if="!agent.confirmed"
              class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-n-amber-3 text-n-amber-11"
            >
              {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
            </span>
          </td>

          <td class="px-3 py-3">
            <div class="flex justify-end gap-1">
              <Button
                v-if="showEditAction(agent)"
                v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                icon="i-lucide-pen"
                slate
                xs
                faded
                @click.stop="openEditPopup(agent)"
              />
              <Button
                v-if="showDeleteAction(agent)"
                v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :is-loading="loading[agent.id]"
                @click.stop="openDeletePopup(agent, index)"
              />
            </div>
          </td>
        </tr>

        <!-- Os NOMES de caixas e times: nove colunas nao cabem numa linha so,
             entao o detalhe mora aqui embaixo. -->
        <tr v-if="expandidos.has(agent.id)" class="bg-n-alpha-1">
          <td colspan="9" class="px-3 pb-4 pt-1">
            <div class="grid gap-5 sm:grid-cols-3 pl-8">
              <div>
                <h4
                  class="mb-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-n-slate-9"
                >
                  {{
                    $t('AGENT_MGMT.LIST.DETAIL.INBOXES', {
                      n: (dadosDe(agent).inboxes || []).length,
                    })
                  }}
                </h4>
                <div class="flex flex-col gap-1.5">
                  <div
                    v-for="caixa in dadosDe(agent).inboxes || []"
                    :key="caixa.name"
                    class="flex flex-wrap items-center gap-1.5 text-[13px] text-n-slate-11"
                  >
                    {{ caixa.name }}
                    <span
                      v-if="caixa.viewer"
                      class="rounded px-1.5 py-0.5 text-[10.5px] font-semibold bg-n-slate-3 text-n-slate-11"
                    >
                      {{ $t('AGENT_MGMT.LIST.DETAIL.VIEW_ONLY') }}
                    </span>
                    <span
                      v-else-if="!caixa.auto_assign"
                      class="rounded px-1.5 py-0.5 text-[10.5px] font-semibold bg-n-amber-3 text-n-amber-11"
                    >
                      {{ $t('AGENT_MGMT.LIST.DETAIL.NO_AUTO') }}
                    </span>
                  </div>
                  <span
                    v-if="!(dadosDe(agent).inboxes || []).length"
                    class="text-[13px] italic text-n-slate-9"
                  >
                    {{ $t('AGENT_MGMT.LIST.DETAIL.NO_INBOXES') }}
                  </span>
                </div>
              </div>

              <div>
                <h4
                  class="mb-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-n-slate-9"
                >
                  {{
                    $t('AGENT_MGMT.LIST.DETAIL.TEAMS', {
                      n: (dadosDe(agent).teams || []).length,
                    })
                  }}
                </h4>
                <div class="flex flex-col gap-1.5">
                  <div
                    v-for="time in dadosDe(agent).teams || []"
                    :key="time"
                    class="text-[13px] text-n-slate-11"
                  >
                    {{ time }}
                  </div>
                  <span
                    v-if="!(dadosDe(agent).teams || []).length"
                    class="text-[13px] italic text-n-slate-9"
                  >
                    {{ $t('AGENT_MGMT.LIST.DETAIL.NO_TEAMS') }}
                  </span>
                </div>
              </div>

              <div>
                <h4
                  class="mb-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-n-slate-9"
                >
                  {{ $t('AGENT_MGMT.LIST.DETAIL.ACTIVITY') }}
                </h4>
                <div class="flex flex-col gap-1.5 text-[13px] text-n-slate-11">
                  <span>
                    {{
                      $t('AGENT_MGMT.LIST.DETAIL.WAITING_OF', {
                        waiting: dadosDe(agent).waiting || 0,
                        open: dadosDe(agent).open || 0,
                      })
                    }}
                  </span>
                  <span>
                    {{
                      $t('AGENT_MGMT.LIST.DETAIL.RESOLVED', {
                        n: dadosDe(agent).resolved_7d || 0,
                      })
                    }}
                  </span>
                  <span>{{ ultimaAtividade(agent).texto }}</span>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </template>
    </SettingsTable>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :custom-role-id="currentAgent.custom_role_id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
    <!-- eslint-disable-next-line vue/no-static-inline-styles -->
    <Teleport to="body">
      <div
        v-if="permissoesPopover.visivel"
        class="fixed z-50 max-w-[300px] bg-n-solid-2 rounded-xl border border-n-weak shadow-lg pointer-events-none"
        :style="{
          left: `${permissoesPopover.x}px`,
          top: `${permissoesPopover.y}px`,
        }"
      >
        <div class="flex flex-col gap-1 p-4">
          <span class="font-semibold text-sm text-n-slate-12">
            {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
          </span>
          <ul class="pl-4 mb-0 list-disc">
            <li
              v-for="permission in permissoesPopover.itens"
              :key="permission"
              class="font-normal text-sm text-n-slate-11"
            >
              {{ permissionLabel(permission) }}
            </li>
          </ul>
        </div>
      </div>
    </Teleport>
  </SettingsPageLayout>
</template>
